<template>
  <div
    class="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
    style="padding-bottom: calc(4rem + env(safe-area-inset-bottom))"
    @click.self="close"
  >
    <form
      class="w-full max-h-[85dvh] flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden"
      @submit.prevent="save"
    >
      <div class="flex items-center justify-between px-4 py-3 bg-dark-primary">
        <h2 class="text-lg font-semibold text-white">Add contact</h2>
        <button type="button" class="text-2xl leading-none text-white px-2" @click="close">×</button>
      </div>

      <div class="flex-1 overflow-y-auto p-4 space-y-4">
        <div class="space-y-2">
          <div class="font-semibold text-gray-800">Name</div>
          <input v-model="contactData.prefix" type="text" placeholder="Prefix" :class="inputClass">
          <input v-model="contactData.first_name" type="text" placeholder="First *" :class="inputClass">
          <input v-model="contactData.preferred_name" type="text" placeholder="Preferred" :class="inputClass">
          <input v-model="contactData.middle_name" type="text" placeholder="Middle" :class="inputClass">
          <input v-model="contactData.last_name" type="text" placeholder="Last *" :class="inputClass">
          <input v-model="contactData.suffix" type="text" placeholder="Suffix" :class="inputClass">
        </div>

        <div class="space-y-2">
          <div class="font-semibold text-gray-800">Company info</div>
          <input v-model="companyRelationshipData.title" type="text" placeholder="Title" :class="inputClass">
          <input v-model="companyRelationshipData.email_address" type="email" placeholder="Company email" :class="inputClass">
          <input v-model="companyRelationshipData.phone_number" type="tel" maxlength="50" placeholder="Company phone" :class="inputClass">
          <textarea v-model="companyRelationshipData.contact_notes" rows="3" placeholder="Notes" :class="inputClass"></textarea>
        </div>

        <div class="space-y-2">
          <div class="font-semibold text-gray-800">Personal info</div>
          <input v-model="contactData.personal_email" type="email" placeholder="Personal email" :class="inputClass">
          <input v-model="contactData.personal_phone" type="tel" maxlength="50" placeholder="Personal phone" :class="inputClass">
          <input v-model="contactData.linkedin_profile" type="text" placeholder="LinkedIn profile" :class="inputClass">
        </div>

        <label class="flex items-center space-x-2 text-base">
          <input v-model="companyRelationshipData.is_primary_contact" type="checkbox" class="h-5 w-5 rounded">
          <span>Primary contact</span>
        </label>

      </div>

      <!-- Outside the scrolling body, so Save and Cancel stay in view -->
      <div class="px-4 py-2 space-y-2 border-t border-gray-200">
        <div v-if="errorMessage" class="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm p-3">{{ errorMessage }}</div>
        <div class="grid grid-cols-2 gap-2">
          <button type="button" class="py-2 rounded-md border border-gray-300 text-gray-700" @click="close">Cancel</button>
          <button type="submit" class="py-2 rounded-md bg-dark-primary text-white disabled:opacity-50" :disabled="isSaving">
            {{ isSaving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { createContactAndCompanyRelationship } from '@/services/Contact';

const props = defineProps({
  company: Object
})

const emit = defineEmits(['close', 'contact-added'])

const store = useStore()

const NOT_ADDED_MESSAGE = 'Error: Contact was not added. Check your connection and retry'

const inputClass = 'appearance-none w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base'

const contactData = ref({
  prefix: null,
  first_name: null,
  preferred_name: null,
  middle_name: null,
  last_name: null,
  suffix: null,
  personal_phone: null,
  personal_email: null,
  linkedin_profile: null,
  personal_notes: null,
})

const companyRelationshipData = ref({
  email_address: null,
  contact_notes: null,
  title: null,
  phone_number: null,
  is_primary_contact: false,
  status: 'active',
  company_id: props.company.id
})

const isSaving = ref(false)
const errorMessage = ref('')

function close() {
  if (!isSaving.value) {
    emit('close')
  }
}

async function save() {
  const missing = []
  if (!contactData.value.first_name) missing.push('first name')
  if (!contactData.value.last_name) missing.push('last name')
  if (missing.length > 0) {
    errorMessage.value = `Error: Missing ${missing.join(' and ')}`
    return
  }

  errorMessage.value = ''
  isSaving.value = true
  const data = {
    contact: contactData.value,
    company_relationship: companyRelationshipData.value
  }
  const response = await createContactAndCompanyRelationship(data, store.getters['auth/showToken'])
  isSaving.value = false

  if (response && response.status === 200) {
    emit('contact-added')
    return
  }
  const detail = response && response.response && response.response.data && response.response.data.detail
  errorMessage.value = detail ? `Error: Contact was not added. ${detail}` : NOT_ADDED_MESSAGE
}
</script>
